import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { formatCurrency, formatDate, monthStart, today } from '../utils/helpers';
import { Printer, FileText } from 'lucide-react';

export default function Reports() {
  const [tab, setTab] = useState('pnl');
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [ledgers, setLedgers] = useState([]);
  const [partyId, setPartyId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/ledgers').then(r => setLedgers(r.data.data || []));
  }, []);

  const load = async () => {
    setData(null);
    setError('');
    try {
      let url = '';
      if (tab === 'pnl') url = `/reports/profit-loss?from=${from}&to=${to}`;
      else if (tab === 'bs') url = '/reports/balance-sheet';
      else if (tab === 'stock') url = '/reports/stock';
      else if (tab === 'vehicle-opex') url = '/reports/vehicle-opex';
      else if (tab === 'cashbook') url = `/reports/cashbook?from=${from}&to=${to}`;
      else if (tab === 'gst') url = `/reports/gst?from=${from}&to=${to}`;
      else if (tab === 'ledger-statement' && partyId) url = `/reports/ledger-statement?ledger_id=${partyId}&from=${from}&to=${to}`;
      if (!url) return;
      const r = await api.get(url);
      if (r?.data?.success === false) throw new Error(r.data.message || 'Failed to load report');
      setData(r.data);
    } catch (e) {
      setData(null);
      setError(e?.response?.data?.message || e?.message || 'Failed to load report');
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, from, to, partyId]);

  return (
    <div>
      <div className="sub-tabs">
        <div className={'sub-tab ' + (tab==='pnl'?'active':'')} onClick={()=>setTab('pnl')}>Profit & Loss</div>
        <div className={'sub-tab ' + (tab==='bs'?'active':'')} onClick={()=>setTab('bs')}>Balance Sheet</div>
        <div className={'sub-tab ' + (tab==='stock'?'active':'')} onClick={()=>setTab('stock')}>Stock Report</div>
        <div className={'sub-tab ' + (tab==='vehicle-opex'?'active':'')} onClick={()=>setTab('vehicle-opex')}>Vehicle OPEX</div>
        <div className={'sub-tab ' + (tab==='cashbook'?'active':'')} onClick={()=>setTab('cashbook')}>Cash Book</div>
        <div className={'sub-tab ' + (tab==='gst'?'active':'')} onClick={()=>setTab('gst')}>GST Summary</div>
        <div className={'sub-tab ' + (tab==='ledger-statement'?'active':'')} onClick={()=>{setTab('ledger-statement');setPartyId('');}}>Ledger Statement</div>
      </div>

      <div className="card">
        <div className="search-bar">
          {tab !== 'bs' && tab !== 'stock' && tab !== 'vehicle-opex' && <>
            <label style={{fontSize:12,fontWeight:600}}>From:</label>
            <input type="date" value={from} onChange={e=>setFrom(e.target.value)} style={{width:160}}/>
            <label style={{fontSize:12,fontWeight:600}}>To:</label>
            <input type="date" value={to} onChange={e=>setTo(e.target.value)} style={{width:160}}/>
          </>}
          {tab === 'ledger-statement' && (
            <select value={partyId} onChange={e=>setPartyId(e.target.value)}>
              <option value="">-- Select Ledger --</option>
              {ledgers.map(l=><option key={l.id} value={l.id}>{l.name} ({l.party_type})</option>)}
            </select>
          )}
          <button className="btn btn-blue" onClick={load}><FileText size={13}/> Refresh</button>
          <button className="btn btn-gray" onClick={()=>window.print()}><Printer size={13}/> Print</button>
        </div>

        {!data && !error && <div className="empty">Loading...</div>}
        {!!error && <div className="empty" style={{ color: '#dc2626' }}>{error}</div>}

        {tab === 'pnl' && data && (
          <div className="financial-statement">
            <div style={{textAlign:'center',marginBottom:20}}>
              <h2 style={{fontSize:20,color:'#1e40af',margin:0}}>PROFIT &amp; LOSS STATEMENT</h2>
              <p style={{fontSize:13,color:'#64748b',marginTop:4}}>For the period {formatDate(from)} to {formatDate(to)}</p>
            </div>

            <div className="stats-grid" style={{marginBottom:20}}>
              <div className="stat-card green"><div className="stat-label">Net Revenue</div><div className="stat-value">{formatCurrency(data.net_revenue)}</div><div className="stat-sub">{data.sales_count} invoices</div></div>
              <div className="stat-card"><div className="stat-label">Gross Profit</div><div className="stat-value">{formatCurrency(data.gross_profit)}</div><div className="stat-sub">Margin {data.gross_margin}%</div></div>
              <div className="stat-card purple"><div className="stat-label">Net Profit</div><div className="stat-value" style={{color:data.net_profit>=0?'#059669':'#dc2626'}}>{formatCurrency(data.net_profit)}</div><div className="stat-sub">Margin {data.net_margin}%</div></div>
            </div>

            <table className="statement-table">
              <tbody>
                <tr className="section-row"><td colSpan="2">REVENUE</td></tr>
                <tr><td>Gross Sales</td><td className="td-num">{formatCurrency(data.sales_subtotal)}</td></tr>
                <tr><td style={{paddingLeft:24}}>Less: Sales Discount</td><td className="td-num text-red">({formatCurrency(data.sales_discount)})</td></tr>
                <tr className="subtotal"><td>Net Revenue</td><td className="td-num">{formatCurrency(data.net_revenue)}</td></tr>
                <tr><td>Transport Income</td><td className="td-num">{formatCurrency(data.transport_income)}</td></tr>
                <tr className="section-total"><td>Total Income</td><td className="td-num">{formatCurrency(data.net_revenue + data.transport_income)}</td></tr>

                <tr className="section-row"><td colSpan="2">COST OF GOODS SOLD</td></tr>
                <tr><td>Cost of Goods Sold (COGS)</td><td className="td-num">{formatCurrency(data.cogs)}</td></tr>
                <tr className="section-total"><td>GROSS PROFIT</td><td className="td-num" style={{color:'#059669'}}>{formatCurrency(data.gross_profit)}</td></tr>

                <tr className="section-row"><td colSpan="2">OPERATING EXPENSES</td></tr>
                {data.expense_breakup?.length > 0 ? data.expense_breakup.map((e, i) => (
                  <tr key={i}><td style={{paddingLeft:24}}>{e.name} <small style={{color:'#94a3b8'}}>({e.type})</small></td><td className="td-num">{formatCurrency(e.total)}</td></tr>
                )) : <tr><td colSpan="2" style={{textAlign:'center',color:'#94a3b8',padding:14}}>No operating expenses in this period</td></tr>}
                <tr className="section-total"><td>Total Operating Expenses</td><td className="td-num text-red">({formatCurrency(data.total_expenses)})</td></tr>

                <tr className="grand-total"><td>NET PROFIT / (LOSS)</td><td className="td-num" style={{color:data.net_profit>=0?'#059669':'#dc2626'}}>{formatCurrency(data.net_profit)}</td></tr>
              </tbody>
            </table>

            <div style={{marginTop:20,padding:14,background:'#f8fafc',borderRadius:8,fontSize:12,color:'#64748b'}}>
              <strong>Note:</strong> COGS is computed as (Quantity sold × Purchase Price) per product. 
              Vehicle costs (Fuel {formatCurrency(data.fuel_cost)} + Maintenance {formatCurrency(data.maintenance_cost)} + Trips {formatCurrency(data.trip_cost)}) 
              are included above where categorized.
            </div>
          </div>
        )}

        {tab === 'bs' && data && (
          <div className="financial-statement">
            <div style={{textAlign:'center',marginBottom:20}}>
              <h2 style={{fontSize:20,color:'#1e40af',margin:0}}>BALANCE SHEET</h2>
              <p style={{fontSize:13,color:'#64748b',marginTop:4}}>As of {formatDate(data.as_of || today())}</p>
            </div>

            <div className="two-col-grid">
              <div>
                <table className="statement-table">
                  <thead><tr><th colSpan="2" style={{background:'#dbeafe',color:'#1e40af',padding:10,fontSize:13}}>ASSETS</th></tr></thead>
                  <tbody>
                    <tr className="section-row"><td colSpan="2">Current Assets</td></tr>
                    <tr><td>Cash in Hand</td><td className="td-num">{formatCurrency(data.assets?.cash || 0)}</td></tr>
                    <tr><td>Bank Accounts</td><td className="td-num">{formatCurrency(data.assets?.bank || 0)}</td></tr>
                    {data.assets?.bank_accounts?.filter(b=>b.account_type!=='cash' && !['od','cc','loan'].includes(b.account_type)).map(b=>(
                      <tr key={b.id}><td style={{paddingLeft:24,fontSize:12,color:'#64748b'}}>— {b.bank_name}</td><td className="td-num" style={{fontSize:12,color:'#64748b'}}>{formatCurrency(b.current_balance)}</td></tr>
                    ))}
                    <tr><td>Inventory</td><td className="td-num">{formatCurrency(data.assets?.inventory || 0)}</td></tr>
                    <tr><td>Accounts Receivable</td><td className="td-num">{formatCurrency(data.assets?.receivables || 0)}</td></tr>
                    {(data.assets?.ledger_opening || 0) > 0 && <tr><td style={{paddingLeft:24,fontSize:12,color:'#64748b'}}>— Opening receivables</td><td className="td-num" style={{fontSize:12,color:'#64748b'}}>{formatCurrency(data.assets?.ledger_opening || 0)}</td></tr>}
                    <tr className="section-row"><td colSpan="2">Fixed Assets</td></tr>
                    <tr><td>Vehicles (at cost)</td><td className="td-num">{formatCurrency(data.assets?.vehicles || 0)}</td></tr>
                    <tr className="grand-total" style={{background:'#dbeafe'}}><td>TOTAL ASSETS</td><td className="td-num">{formatCurrency(data.assets?.total || 0)}</td></tr>
                  </tbody>
                </table>
              </div>

              <div>
                    <table className="statement-table">
                      <thead><tr><th colSpan="2" style={{background:'#fee2e2',color:'#991b1b',padding:10,fontSize:13}}>LIABILITIES &amp; EQUITY</th></tr></thead>
                      <tbody>
                        <tr className="section-row"><td colSpan="2">Current Liabilities</td></tr>
                        <tr><td>Accounts Payable</td><td className="td-num">{formatCurrency(data.liabilities?.payables || 0)}</td></tr>
                        {(data.liabilities?.ledger_opening || 0) > 0 && <tr><td style={{paddingLeft:24,fontSize:12,color:'#64748b'}}>— Opening payables</td><td className="td-num" style={{fontSize:12,color:'#64748b'}}>{formatCurrency(data.liabilities?.ledger_opening || 0)}</td></tr>}
                        {(data.liabilities?.loans || 0) > 0 && <>
                          <tr className="section-row"><td colSpan="2">Long-term Liabilities</td></tr>
                          <tr><td>Bank Loans / OD / CC</td><td className="td-num">{formatCurrency(data.liabilities?.loans || 0)}</td></tr>
                        </>}
                        <tr className="subtotal"><td>Total Liabilities</td><td className="td-num">{formatCurrency(data.liabilities?.total || 0)}</td></tr>

                        <tr className="section-row"><td colSpan="2">Equity</td></tr>
                        <tr><td>Owner's Equity (derived)</td><td className="td-num">{formatCurrency(data.equity || 0)}</td></tr>
                        <tr className="grand-total" style={{background:'#fee2e2'}}><td>TOTAL LIABILITIES + EQUITY</td><td className="td-num">{formatCurrency((data.liabilities?.total || 0) + (data.equity || 0))}</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>

            <div style={{marginTop:16,padding:12,borderRadius:8,background:data.balance_check?'#d1fae5':'#fee2e2',color:data.balance_check?'#065f46':'#991b1b',textAlign:'center',fontSize:13,fontWeight:600}}>
              {data.balance_check ? '✓ Balance Sheet balances correctly' : '⚠ Balance sheet does not balance — check data integrity'}
            </div>
          </div>
        )}

        {tab === 'stock' && data && (
          <div>
            <h3 style={{marginBottom:10}}>Stock Report — Total Value: {formatCurrency(data.total_value)}</h3>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Code</th><th>Product</th><th>Category</th><th>Unit</th><th className="td-num">Stock</th><th className="td-num">Min</th><th className="td-num">Buy Price</th><th className="td-num">Stock Value</th></tr></thead>
                <tbody>
                  {data.data?.map(p => (
                    <tr key={p.id}>
                      <td>{p.product_code}</td>
                      <td>{p.name}</td>
                      <td>{p.category_name || '-'}</td>
                      <td>{p.unit}</td>
                      <td className={'td-num ' + (parseFloat(p.stock_qty)<=parseFloat(p.min_stock)?'text-red':'text-green')}>{p.stock_qty}</td>
                      <td className="td-num">{p.min_stock}</td>
                      <td className="td-num">{formatCurrency(p.purchase_price)}</td>
                      <td className="td-num">{formatCurrency(p.stock_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'vehicle-opex' && data && (
          <div>
            <h3 style={{marginBottom:10}}>Vehicle Operating Expenses</h3>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Vehicle</th><th>Type</th><th>Driver</th><th className="td-num">Total KM</th><th className="td-num">Fuel</th><th className="td-num">Maintenance</th><th className="td-num">Trip Exp</th><th className="td-num">Other</th><th className="td-num">Total OPEX</th></tr></thead>
                <tbody>
                  {data.data?.map(v => (
                    <tr key={v.id}>
                      <td><strong>{v.vehicle_no}</strong></td>
                      <td>{v.vehicle_type}</td>
                      <td>{v.driver_name}</td>
                      <td className="td-num">{v.total_km}</td>
                      <td className="td-num">{formatCurrency(v.fuel_cost)}</td>
                      <td className="td-num">{formatCurrency(v.maintenance_cost)}</td>
                      <td className="td-num">{formatCurrency(v.trip_expenses)}</td>
                      <td className="td-num">{formatCurrency(v.other_expenses)}</td>
                      <td className="td-num"><strong>{formatCurrency(v.total_opex)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'cashbook' && data && (
          <div>
            <h3 style={{marginBottom:10}}>Cash Book ({formatDate(from)} - {formatDate(to)})</h3>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date</th><th>Ref</th><th>Type</th><th>Mode</th><th>Notes</th><th className="td-num">Amount</th></tr></thead>
                <tbody>
                  {data.data?.map((r, i) => (
                    <tr key={i}>
                      <td>{formatDate(r.date)}</td>
                      <td>{r.ref}</td>
                      <td><span className={'badge ' + (r.type==='received'?'badge-green':r.type==='expense'?'badge-red':'badge-orange')}>{r.type}</span></td>
                      <td>{r.payment_mode}</td>
                      <td>{r.notes||'-'}</td>
                      <td className={'td-num ' + (r.amount>=0?'text-green':'text-red')}>{formatCurrency(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'gst' && data && (
          <div>
            <h3 style={{marginBottom:14}}>GST Summary ({formatDate(from)} - {formatDate(to)})</h3>
            <div className="stats-grid">
              <div className="stat-card green"><div className="stat-label">Output Tax (Sales GST)</div><div className="stat-value">{formatCurrency(data.output_tax)}</div></div>
              <div className="stat-card orange"><div className="stat-label">Input Tax (Purchase GST)</div><div className="stat-value">{formatCurrency(data.input_tax)}</div></div>
              <div className="stat-card red"><div className="stat-label">Net GST Payable</div><div className="stat-value">{formatCurrency(data.net_payable)}</div></div>
            </div>
          </div>
        )}

        {tab === 'ledger-statement' && data && (
          <div>
            <h3 style={{marginBottom:10}}>Ledger ({formatDate(from)} - {formatDate(to)})</h3>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date</th><th>Reference</th><th>Type</th><th className="td-num">Debit</th><th className="td-num">Credit</th><th className="td-num">Balance</th></tr></thead>
                <tbody>
                  {data.data?.length ? data.data.map((r, i) => (
                    <tr key={i}>
                      <td>{formatDate(r.date)}</td>
                      <td>{r.ref}</td>
                      <td>{r.type}</td>
                      <td className="td-num">{formatCurrency(r.debit)}</td>
                      <td className="td-num">{formatCurrency(r.credit)}</td>
                      <td className="td-num"><strong>{formatCurrency(r.balance)}</strong></td>
                    </tr>
                  )) : <tr><td colSpan="6" className="empty">No records in this period</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
